import type {OverviewPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useCurrentUser} from '@github-ui/current-user'
import {dismissRepositoryNoticePathPath, repositoryDeployPath} from '@github-ui/paths'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {RocketIcon} from '@primer/octicons-react'
import {Banner} from '@primer/react'
import {useState} from 'react'

const FRAMEWORKS = {
  vite: 'Vite app',
  astro: 'Astro app',
  // TODO: Add support for more frameworks
} as const

interface DeployBannerProps {
  detectedStack?: OverviewPayload['banners']['detectedStack']
}

export function DeployBanner({detectedStack}: DeployBannerProps) {
  const [hidden, setHidden] = useState(false)
  const currentUser = useCurrentUser()
  const repo = useCurrentRepository()

  const deployPath = repositoryDeployPath({
    owner: repo.ownerLogin,
    repo: repo.name,
    framework: detectedStack?.framework ?? undefined,
    packageManager: detectedStack?.packageManager ?? undefined,
  })

  const handleDismiss = async () => {
    if (!currentUser) return

    const dismissPath = dismissRepositoryNoticePathPath({login: currentUser.login})
    const form = new FormData()
    form.append('_method', 'delete')
    form.append('repository_id', repo.id.toString())
    form.append('notice_name', 'deploy_to_pages_banner')

    try {
      await verifiedFetch(dismissPath, {
        method: 'POST',
        body: form,
      })
    } catch {
      // Silently handle error - banner will still be hidden for better UX
    }

    setHidden(true)
  }

  const framework = detectedStack?.framework
  const description = `Deploy your ${
    framework && framework in FRAMEWORKS ? FRAMEWORKS[framework as keyof typeof FRAMEWORKS] : 'project'
  } to GitHub Pages`

  if (hidden) {
    return null
  }

  return (
    <Banner
      icon={<RocketIcon />}
      // @ts-expect-error The title prop actually accepts ReactNode but there's a bug in the type definition
      title={<span className="text-normal">{description}</span>}
      primaryAction={
        <Banner.PrimaryAction as="a" href={deployPath}>
          Deploy
        </Banner.PrimaryAction>
      }
      secondaryAction={<Banner.SecondaryAction onClick={handleDismiss}>Dismiss</Banner.SecondaryAction>}
    />
  )
}

try{ DeployBanner.displayName ||= 'DeployBanner' } catch {}